/**
 * Applies one agent stream event to a conversation's message list.
 *
 * Extracted from ConversationView's send handler so the same code serves both ways a run
 * reaches the client: the live NDJSON stream, and the snapshot a returning reader polls for
 * after navigating away. Two code paths rendering one run differently is the bug this
 * prevents.
 *
 * Only events that change the message list live here. `status` is transient, `wiki_updated`
 * and `conversation_title` act outside the conversation, and `done`/`error` end the run —
 * the caller keeps those, and this returns the list untouched (by identity) for them.
 */
export interface AgentStreamContext {
  assistantMessageId: string
  isPlanRevision: boolean
}

export function applyAgentEvent(
  messages: Message[],
  event: AgentStreamEvent,
  context: AgentStreamContext
): Message[] {
  const patch = (change: (message: Message) => Message): Message[] =>
    messages.map((message) =>
      message.id === context.assistantMessageId ? change(message) : message
    )

  switch (event.type) {
    case 'token':
      return patch((message) => ({ ...message, content: message.content + event.text }))
    case 'replace':
      return patch((message) => ({ ...message, content: event.text }))
    case 'clarify_actions':
      return patch((message) => ({
        ...message,
        metadata: { ...message.metadata, clarifyActions: event.actions }
      }))
    case 'plan':
      return patch((message) => ({
        ...message,
        metadata: {
          ...message.metadata,
          plan: {
            summary: event.summary,
            content: event.content,
            isRevision: context.isPlanRevision
          }
        }
      }))
    case 'debug':
      return patch((message) => ({
        ...message,
        metadata: { ...message.metadata, debug: event.entries }
      }))
    case 'activity':
      return patch((message) => ({
        ...message,
        metadata: {
          ...message.metadata,
          activity: [...(message.metadata?.activity || []), event.item]
        }
      }))
    default:
      return messages
  }
}

/**
 * Clears the target message's accumulated activity log without touching its other metadata.
 *
 * `activity` is the only metadata field the reducer above appends to rather than overwrites —
 * every other case (plan, clarifyActions, debug) replaces its key wholesale, so replaying a
 * snapshot on top of existing state is naturally idempotent for those. Activity isn't: if the
 * target message already carries entries from a prior live stream or an autosaved snapshot,
 * replaying the run's metadataEvents on top would append a second copy of the whole log. Call
 * this once, before that replay, to make the first replay authoritative the same way the first
 * text delta is.
 */
export function resetRunActivity(messages: Message[], assistantMessageId: string): Message[] {
  return messages.map((message) => {
    if (message.id !== assistantMessageId || !message.metadata?.activity) return message
    const { activity: _activity, ...rest } = message.metadata
    return { ...message, metadata: rest }
  })
}
